<script setup lang="ts">
import BaseIcon from "@/components/Base/Icon/Icon.vue";
import { useFormFieldAware } from "@/composables/useFormFieldAware";
import type { LabelWeight } from "@/types/components/Base/LabelWeight";
import type { ValidationState } from "@/types/vuelidate.types";

defineOptions({ name: "BaseTextInput" });

export type Props = {
  name?: string;
  id?: string;
  label: string;
  showLabel?: boolean;
  labelWeight?: LabelWeight;
  placeholder?: string;
  helpMessage?: string;
  disabled?: boolean;
  size?: "lg" | "md" | "sm";
  fullWidth?: boolean;
  modelValue: string | number | null | undefined;
  maxLength?: number;
  labelTooltip?: string;

  v$?: ValidationState;
  prefixText?: string | null;
  hasMarginBottom?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  name: "",
  placeholder: "",
  helpMessage: "",
  showLabel: true,
  labelWeight: "medium",
  size: "md",
  v$: null,
  prefixText: null,
  hasMarginBottom: true, // in general it might be better to not apply `mb-3` as a default so that this can be set in context i.e. via `flex-stack`
});

const { isWrapped, effectiveId, ariaBindings } = useFormFieldAware(() => ({
  id: props.id,
}));

const inputRef = ref<HTMLInputElement>();

const handleInputEvent = (event: Event): void => {
  if (props.v$) {
    props.v$.$touch();
  }
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const isValid = computed(() => {
  return !props.v$?.$error;
});

const dirty = computed(() => props.v$?.$dirty);
const handleBlurEvent = (): void => {
  if (props.v$) {
    props.v$.$touch();
  }
};

const handleChangeEvent = (): void => {
  if (props.v$) {
    props.v$.$touch();
  }
};

const focus = (): void => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus,
});

const emit = defineEmits(["update:modelValue"]);

const tooltipOptions = computed(() => infoTooltipOptions(props.labelTooltip));
</script>

<template>
  <div
    :class="{
      'has-validation': dirty,
      'mb-3': hasMarginBottom && !isWrapped,
      'w-100': fullWidth,
    }"
  >
    <label
      v-if="!isWrapped"
      :class="{
        'form-label': true,
        [`form-label-${size}`]: true,
        [`fw-${labelWeight}`]: true,
        'visually-hidden': !showLabel,
      }"
      :for="effectiveId"
    >
      {{ label }}
      <BaseIcon
        v-if="labelTooltip"
        v-tooltip="tooltipOptions"
        icon-name="info-circle"
        :size="16"
        class="ms-1"
        style="vertical-align: text-top"
        role="button"
        tabindex="0"
        :aria-label="labelTooltip"
        @click.stop.prevent
      />
    </label>
    <div :class="{ 'input-group': prefixText }">
      <span v-if="prefixText" class="input-group-text">{{ prefixText }}</span>

      <input
        :id="effectiveId"
        ref="inputRef"
        v-bind="{ ...$attrs, ...ariaBindings }"
        :value="modelValue"
        :maxlength="maxLength"
        :disabled="disabled"
        class="form-control"
        :class="{ 'is-invalid': !isValid && dirty }"
        :name="name"
        :placeholder="placeholder"
        :data-testid="`input-${name}`"
        @blur="handleBlurEvent"
        @input="handleInputEvent"
        @change="handleChangeEvent"
      />
    </div>
    <div v-if="!isWrapped && isValid && helpMessage" class="form-text">
      {{ helpMessage }}
    </div>
    <div
      v-if="!isWrapped && !isValid"
      class="invalid-feedback d-flex"
      :data-testid="`input-error-${name}`"
    >
      {{ v$?.$errors[0]?.$message }}
    </div>
  </div>
</template>
